import {
  compressedReceiptName,
  fitReceiptDimensions,
  getReceiptImageProblem,
  isHeicReceipt,
  type ReceiptImageProblem,
  shouldCompressReceipt,
} from "@core/invoices/utils/receipt-image";

export type PreparedReceipt =
  | { file: File; problem?: undefined }
  | { file?: undefined; problem: ReceiptImageProblem };

const JPEG_QUALITY = 0.82;

type DecodedImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
};

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });

    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => bitmap.close(),
    };
  }

  const url = URL.createObjectURL(file);

  try {
    const image = new Image();
    image.src = url;
    await image.decode();

    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

async function encodeJpeg(image: DecodedImage, name: string) {
  const { width, height } = fitReceiptDimensions(image.width, image.height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");

  if (!context) throw new Error("Canvas 2D context unavailable");

  // JPEG has no alpha: paint white under transparent PNG screenshots.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image.source, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );

  if (!blob) throw new Error("Canvas JPEG encoding failed");

  return new File([blob], compressedReceiptName(name), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

/**
 * Turn the photo a student picked into a receipt the server accepts: big
 * camera photos are shrunk to a ≤2000 px JPEG, HEIC is converted when the
 * browser can decode it (Safari), and anything still unusable comes back as
 * a {@link ReceiptImageProblem} to show in the student's language.
 *
 * @example
 * const { file, problem } = await prepareReceiptImage(input.files[0]);
 */
export async function prepareReceiptImage(file: File): Promise<PreparedReceipt> {
  const heic = isHeicReceipt(file);
  const earlyProblem = heic
    ? undefined
    : getReceiptImageProblem(file, Number.POSITIVE_INFINITY);

  if (earlyProblem) return { problem: earlyProblem };

  let prepared: File = file;

  try {
    const image = await decodeImage(file);

    try {
      if (heic || shouldCompressReceipt(file, image.width, image.height)) {
        const compressed = await encodeJpeg(image, file.name);

        if (heic || compressed.size < file.size) prepared = compressed;
      }
    } finally {
      image.release();
    }
  } catch {
    // The browser cannot decode it (e.g. HEIC on Android Chrome): a HEIC is
    // rejected below, any other file is sent as-is for the server to judge.
    if (heic) return { problem: "receiptUnsupportedFormat" };
  }

  const problem = getReceiptImageProblem(prepared);

  return problem ? { problem } : { file: prepared };
}
